import { useEffect, useState } from "react";

import { useAuth } from "../context/auth-context";
import { useConfig } from "../context/config-context";
import FileService from "../services/fileService";
import { filtersToQueryString, removeFilters, updateFilters } from "../utils";
import API from "../utils/api";

import Gabarit from "../components/Gabarit";
import TablePatient from "../components/Patient/Table";

export default function PatientsPage() {
  const [patients, setPatients] = useState({ docs: [], page: 1, totalDocs: 0 });
  const [patientItems, setPatientItems] = useState([]);
  const [prescriberItems, setPrescriberItems] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [tutoMode, setTutoMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResettingDemo, setIsResettingDemo] = useState(false);
  const [patientLimit, setPatientLimit] = useState(25);
  const [patientSort, setPatientSort] = useState(
    `{"lastName":1, "firstName":1}`
  );
  const [patientPage, setPatientPage] = useState(1);
  const [patientFilters, setPatientFilters] = useState({});
  const [prescriberQuery, setPrescriberQuery] = useState([]);
  const [patientQuery, setPatientQuery] = useState([]);

  const { user } = useAuth();
  const siteConfig = useConfig();
  const fileService = new FileService();
  const patientsConfig = siteConfig.pages.patients;
  const featureConfig = siteConfig.features;

  useEffect(() => {
    async function defaultValue() {
      setIsLoading(true);
      const result = await getPatients({
        limit: 25,
        sort: `{"lastName":1, "firstName":1}`,
        page: 1,
        filters: {},
        query: null,
      });
      setPatients(result);

      if (process.env.REACT_APP_ECOSYSTEM === "diabete") await getPumps();
      if (process.env.REACT_APP_ECOSYSTEM === "parkinson")
        await getPrestations();
      setIsLoading(false);
    }
    defaultValue();
  }, []);

  const getPatients = async ({ limit, sort, page, filters, query }) => {
    try {
      const filterQueryString = filtersToQueryString({ filters });
      const response = await API.get(
        `/patients?limit=${limit}&page=${page}&sort=${sort}${
          Array.isArray(query) && query.length > 0 ? "&" + query.join("&") : ""
        }${filterQueryString ? "&" + filterQueryString : ""}`
      );
      return response.data;
    } catch (error) {}
  };

  const getPrescribers = async (query = []) => {
    try {
      const response = await API.get(
        `/prescribers/search?limit=${5}&page=${1}&sort=${'{"lastName":1, "firstName":1}'}&${query.join(
          "&"
        )}`
      );
      return response?.data?.docs || [];
    } catch (error) {}
  };

  const getPumps = async () => {
    try {
      const response = await API.get(`/devices/pumps`);
      setPumps(response.data);
    } catch (error) {}
  };

  const getPrestations = async () => {
    try {
      const response = await API.get(`/contracts/prestations`);
      setPrestations(response.data);
    } catch (error) {}
  };

  const updatePatientFilters = async (
    operator,
    filterName,
    operation,
    value
  ) => {
    const filters = updateFilters(
      patientFilters,
      operator,
      filterName,
      operation,
      value
    );

    setPatientFilters(filters);
    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      limit: patientLimit,
      sort: patientSort,
      page: 1,
      filters,
      query: [...patientQuery, ...prescriberQuery],
    });
    setPatients(result);
    setIsLoading(false);
    return filters;
  };

  const removePatientFilter = async (
    groupOperator,
    fieldToRemove,
    operationToRemove
  ) => {
    const filters = removeFilters(
      patientFilters,
      groupOperator,
      fieldToRemove,
      operationToRemove
    );
    setPatientFilters(filters);
    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      limit: patientLimit,
      page: 1,
      sort: patientSort,
      filters,
      query: [...patientQuery, ...prescriberQuery],
    });
    setPatients(result);
    setIsLoading(false);
    // Retourner l'objet filters mis à jour
    return filters;
  };

  const removeAllPatientFilters = async () => {
    setPrescriberItems([]);
    setPatientItems([]);
    setPatientFilters({});
    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      limit: patientLimit,
      sort: patientSort,
      page: 1,
      filters: {},
      query: [],
    });
    setPatients(result);
    setIsLoading(false);
  };

  const searchPatients = async (word) => {
    let items = [];
    let query = [];
    const checkString = word && word !== " ";
    if (checkString && /^[a-zA-Z ]+$/.test(word)) {
      query.push(
        `or={"firstName":{"$regex":"${word}", "$options": "i"}}`,
        `or={"lastName":{"$regex":"${word}", "$options": "i"}}`,
        `or={"fullName":{"$regex":"${word}", "$options": "i"}}`
      );
    } else if (checkString && /^\d+$/.test(word)) {
      query.push(`or={"_id":{"$regex":"${parseInt(word, 10)}"}}`);
    }

    const response =
      query.length > 0
        ? await getPatients({
            limit: patientLimit,
            page: 1,
            sort: patientSort,
            filters: patientFilters,
            query,
          })
        : [];

    if (response && response.docs) {
      response.docs.map((item) =>
        items.push({
          label: `${item.fullName} (${item._id})`,
          value: item._id,
          role: "patient",
        })
      );
    }

    return { loading: false, items, groupBy: "patient" };
  };

  const removePatientItem = async (removedItem) => {
    const query = [];
    let items = patientItems;

    items = items.filter((item) => item !== removedItem);

    if (items.length > 0) {
      items.map((item) => query.push(`or={"_id":{"$eq":${item}}}`));
    }
    setPatientItems(items);
    setPatientQuery(query);

    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      limit: patientLimit,
      page: 1,
      sort: patientSort,
      filters: patientFilters,
      query: [...query, ...prescriberQuery],
    });
    setPatients(result);
    setIsLoading(false);
  };

  const updatePatientItem = async (updatedItems) => {
    const query = [];

    if (updatedItems.length > 0) {
      updatedItems.map((item) => query.push(`or={"_id":{"$eq":${item}}}`));
    }
    setPatientItems(updatedItems);
    setPatientQuery(query);

    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      limit: patientLimit,
      page: 1,
      sort: patientSort,
      filters: patientFilters,
      query: [...query, ...prescriberQuery],
    });
    setPatients(result);
    setIsLoading(false);
  };

  const searchPrescribers = async (word) => {
    let items = [];
    let filters = [];
    const checkString = word && word !== " ";
    if (checkString && /^[a-zA-Z ]+$/.test(word)) {
      filters.push(
        `or={"user.firstName":{"$regex":"${word}", "$options": "i"}}`,
        `or={"user.lastName":{"$regex":"${word}", "$options": "i"}}`,
        `or={"user.fullName":{"$regex":"${word}", "$options": "i"}}`
      );
    } else if (checkString && /^\d+$/.test(word)) {
      filters.push(`or={"_id":{"$regex":"${parseInt(word, 10)}"}}`);
    }

    const response = filters.length > 0 ? await getPrescribers(filters) : [];
    response.map((item) =>
      items.push({
        label: `${item.user.fullName} (${item._id})`,
        value: item._id,
        role: item.user.role,
      })
    );

    return { loading: false, items, groupBy: "prescriber" };
  };

  const removePrescriberItem = async (removedItem) => {
    const query = [];
    let items = prescriberItems;

    items = items.filter((item) => item !== removedItem);

    if (items.length > 0) {
      items.map((item) => query.push(`or={"prescriberId":{"$eq":${item}}}`));
      query.push(`or={"prescriberIds":{"$in":[${items}]}}`);
    }
    setPrescriberItems(items);
    setPrescriberQuery(query);

    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      limit: patientLimit,
      page: 1,
      sort: patientSort,
      filters: patientFilters,
      query: [...query, ...patientQuery],
    });
    setPatients(result);
    setIsLoading(false);
  };

  const updatePrescriberItem = async (updatedItems) => {
    const query = [];

    if (updatedItems.length > 0) {
      updatedItems.map((item) =>
        query.push(`or={"prescriberId":{"$eq":${item}}}`)
      );
      query.push(`or={"prescriberIds":{"$in":[${updatedItems}]}}`);
    }
    setPrescriberItems(updatedItems);
    setPrescriberQuery(query);

    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      limit: patientLimit,
      page: 1,
      sort: patientSort,
      filters: patientFilters,
      query: [...query, ...patientQuery],
    });
    setPatients(result);
    setIsLoading(false);
  };

  const handleSort = async (sort) => {
    setPatientSort(sort);
    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      sort,
      limit: patientLimit,
      page: 1,
      filters: patientFilters,
      query: [...patientQuery, ...prescriberQuery],
    });
    setPatients(result);
    setIsLoading(false);
    return sort;
  };

  const handleLimit = async (limit) => {
    setPatientLimit(limit);
    setPatientPage(1);
    setIsLoading(true);
    const result = await getPatients({
      sort: patientSort,
      limit,
      page: 1,
      filters: patientFilters,
      query: [...patientQuery, ...prescriberQuery],
    });
    setPatients(result);
    setIsLoading(false);
    return limit;
  };

  const handlePage = async (page) => {
    setPatientPage(page);
    setIsLoading(true);
    const result = await getPatients({
      sort: patientSort,
      limit: patientLimit,
      page,
      filters: patientFilters,
      query: [...patientQuery, ...prescriberQuery],
    });
    setPatients(result);
    setIsLoading(false);
    return page;
  };

  const handleResetDemo = async () => {
    try {
      setIsResettingDemo(true);
      await API.put("/yousign/reset");
      setIsLoading(true);
      const result = await getPatients({
        limit: patientLimit,
        sort: patientSort,
        page: 1,
        filters: patientFilters,
        query: [...patientQuery, ...prescriberQuery],
      });
      setPatients(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsResettingDemo(false);
      setIsLoading(false);
    }
  };

  return (
    <Gabarit
      label={patientsConfig.title}
      className="flex flex-col gap-3 md:flex-row"
      tutoMode={tutoMode}
      setTutoMode={setTutoMode}
    >
      <siteConfig.components.table_patient.filters
        pumps={pumps}
        prestations={prestations}
        tutoMode={tutoMode}
        isLoading={isLoading}
        page={patientPage}
        filters={patientFilters}
        handleFilters={updatePatientFilters}
        handleRemoveFilter={removePatientFilter}
        handleRemoveAllFilters={removeAllPatientFilters}
        patientItems={patientItems}
        onChangePatient={updatePatientItem}
        onRemovePatient={removePatientItem}
        onSearchPatients={searchPatients}
        prescriberItems={prescriberItems}
        onSearchPrescribers={searchPrescribers}
        onChangePrescriber={updatePrescriberItem}
        onRemovePrescriber={removePrescriberItem}
        firstLogin={user.firstLogin}
        searchByPatient={
          featureConfig.searchPatientByPatient &&
          patientsConfig.authorization.searchByPatient.includes(user.role)
        }
        searchByPrescriber={
          featureConfig.searchPatientByPrescriber &&
          patientsConfig.authorization.searchByPrescriber.includes(user.role)
        }
        searchByPrestation={
          featureConfig.searchPatientByPrestation &&
          patientsConfig.authorization.searchByPrestation.includes(user.role)
        }
        searchByRenewalStatus={patientsConfig.authorization.searchByRenewalStatus.includes(
          user.role,
        )}
        onResetDemo={handleResetDemo}
        isResettingDemo={isResettingDemo}
      />

      <TablePatient
        patients={patients}
        tutoMode={tutoMode}
        isLoading={isLoading}
        limit={patientLimit}
        handleLimit={handleLimit}
        handleSort={handleSort}
        handlePage={handlePage}
        handleDocument={fileService.download}
      />
    </Gabarit>
  );
}
